import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2, ShoppingBag, ArrowRight, ChevronRight } from 'lucide-react';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';
import './Wishlist.css';

const Wishlist = () => {
  const { wishlistItems, removeFromWishlist, clearWishlist, moveToCart } = useWishlist();
  const { addToCart, openCart } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleMoveToCart = (product) => {
    addToCart(product, 1, product.sizes?.[0] || 'One Size', product.colors?.[0] || { name: 'Default', hex: '#000000' });
    removeFromWishlist(product.id);
    openCart();
  };

  const handleAddAllToCart = () => {
    wishlistItems.forEach((product) => {
      addToCart(product, 1, product.sizes?.[0] || 'One Size', product.colors?.[0] || { name: 'Default', hex: '#000000' });
    });
    clearWishlist();
    openCart();
  };

  return (
    <div className="wishlist-page">
      {/* Breadcrumb */}
      <nav className="wishlist-breadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight size={14} />
        <span>Wishlist</span>
      </nav>

      {/* Header */}
      <div className="wishlist-header">
        <div className="wishlist-header-content">
          <h1 className="wishlist-title">
            <Heart size={28} />
            My Wishlist
          </h1>
          <p className="wishlist-count">
            {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'}
          </p>
        </div>
        {wishlistItems.length > 0 && (
          <div className="wishlist-header-actions">
            <button className="wishlist-clear-btn" onClick={clearWishlist}>
              <Trash2 size={16} />
              Clear All
            </button>
            <button className="wishlist-add-all-btn" onClick={handleAddAllToCart}>
              <ShoppingBag size={16} />
              Add All to Bag
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {wishlistItems.length > 0 ? (
        <div className="wishlist-content">
          <div className="wishlist-grid">
            {wishlistItems.map((product) => (
              <div key={product.id} className="wishlist-card">
                <button
                  className="wishlist-remove-btn"
                  onClick={() => removeFromWishlist(product.id)}
                  title="Remove from Wishlist"
                >
                  <Trash2 size={16} />
                </button>

                <Link to={`/product/${product.id}`} className="wishlist-card-image">
                  <img src={product.image} alt={product.name} />
                  {product.badge && <span className="wishlist-card-badge">{product.badge}</span>}
                </Link>

                <div className="wishlist-card-content">
                  <Link to={`/product/${product.id}`}>
                    <p className="wishlist-card-brand">{product.brand}</p>
                    <h3 className="wishlist-card-name">{product.name}</h3>
                  </Link>

                  <div className="wishlist-card-rating">
                    <div className="wishlist-card-stars">
                      {[...Array(5)].map((_, i) => (
                        <span
                          key={i}
                          className={`wishlist-star ${i < Math.floor(product.rating) ? 'filled' : ''}`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <span className="wishlist-reviews">({product.reviews})</span>
                  </div>

                  <div className="wishlist-card-pricing">
                    <span className="wishlist-card-price">
                      ${(product.salePrice || product.price).toFixed(2)}
                    </span>
                    {product.salePrice && (
                      <>
                        <span className="wishlist-card-original-price">
                          ${product.price.toFixed(2)}
                        </span>
                        <span className="wishlist-card-discount">
                          {Math.round(((product.price - product.salePrice) / product.price) * 100)}% OFF
                        </span>
                      </>
                    )}
                  </div>

                  {product.colors && product.colors.length > 0 && (
                    <div className="wishlist-card-colors">
                      {product.colors.slice(0, 4).map((color) => (
                        <span
                          key={color.name}
                          className="wishlist-color-swatch"
                          style={{ backgroundColor: color.hex }}
                          title={color.name}
                        />
                      ))}
                      {product.colors.length > 4 && (
                        <span className="wishlist-color-more">+{product.colors.length - 4}</span>
                      )}
                    </div>
                  )}

                  <button
                    className="wishlist-card-add-btn"
                    onClick={() => handleMoveToCart(product)}
                  >
                    <ShoppingBag size={16} />
                    Add to Bag
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="wishlist-empty">
          <div className="wishlist-empty-icon">
            <Heart size={64} />
          </div>
          <h2 className="wishlist-empty-title">Your wishlist is empty</h2>
          <p className="wishlist-empty-text">
            Save items you love by clicking the heart icon on any product.
          </p>
          <Link to="/shop" className="wishlist-empty-btn">
            Start Shopping
            <ArrowRight size={18} />
          </Link>
        </div>
      )}

      {/* Suggestions Section */}
      {wishlistItems.length === 0 && (
        <div className="wishlist-suggestions">
          <h3 className="wishlist-suggestions-title">Popular Items You Might Like</h3>
          <div className="wishlist-suggestions-links">
            <Link to="/shop?search=dresses">Dresses</Link>
            <Link to="/shop?search=sneakers">Sneakers</Link>
            <Link to="/shop?search=handbags">Handbags</Link>
            <Link to="/shop?search=watches">Watches</Link>
            <Link to="/shop?search=jewelry">Jewelry</Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wishlist;
